// MARK://dhk8/admin-shell/lib/clients/customers
// Clientes / CRM → dhk8-crm-contacts.
//   GET    /api/contacts?tenantId=...&q=...   → { items: ApiContact[], total, page, limit }
//   GET    /api/contacts/:id                  → ApiContact
//   POST   /api/contacts                      → ApiContact
//   PATCH  /api/contacts/:id                  → ApiContact
//   GET    /api/loyalty/config                → LoyaltyConfig
//   PUT    /api/loyalty/config                → LoyaltyConfig
//
// CONTRACT NOTE: el servicio expone sus rutas bajo `/api/...` (NO `/v1/...`).
// El ingress mapea `/v1/dhk8-crm-contacts/(.*)` → `/(.*)` en el pod, así que
// svcUrl('dhk8-crm-contacts', '/api/contacts') resuelve correcto. Las respuestas
// vienen envueltas en el sobre `{ ok, data }` → usar apiData.
//
// Registro del cliente:
//   - personal: nombres/apellidos, teléfonos, correos, DIRECCIONES múltiples.
//   - fiscal: un cliente puede dar de alta VARIAS entidades fiscales (razones
//     sociales) a las que se le emiten CFDI. La info fiscal del cliente mismo
//     es opcional — vive en estas entidades.
// Lealtad: puntos + visitas en el contacto; niveles (tiers) configurables por
// tenant. El nivel se deriva de los puntos contra los umbrales.
import { requireTenant } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import {
  AdminContactsQuery,
  AdminContactQuery,
  AdminCreateContactMutation,
  AdminUpdateContactMutation,
  AdminSaveLoyaltyConfigMutation,
  AdminLoyaltyConfigQuery,
} from '../queries/contacts.queries.js'


export interface ContactPhone { id?: string; phone: string; label: string; isPrimary: boolean }
export interface ContactEmail { id?: string; email: string; label: string; isPrimary: boolean }
export interface ContactAddress {
  id?: string
  label: string
  street: string | null
  city: string | null
  state: string | null
  country: string | null
  zip: string | null
  isPrimary: boolean
}
export interface FiscalEntity {
  id?: string
  rfc: string
  legalName: string
  regimeCode: string
  zipCode: string
  usoCfdi: string
  invoiceEmail: string | null
  isDefault: boolean
}

export interface Customer {
  id: string
  tenantId: string
  firstName: string
  lastName: string | null
  fullName: string
  source: string
  notes: string | null
  tags: string[]
  lastVisitAt: string | null
  totalOrdersCount: number
  totalSpentCents: string
  loyaltyPoints: number
  loyaltyVisits: number
  phones: ContactPhone[]
  emails: ContactEmail[]
  addresses: ContactAddress[]
  fiscalEntities: FiscalEntity[]
  createdAt: string
  updatedAt: string
}

export interface LoyaltyTier {
  id?: string
  name: string
  threshold: number
  color: string | null
}
export interface LoyaltyConfig {
  tenantId: string
  enabled: boolean
  pointsPerVisit: number
  tiers: LoyaltyTier[]
}

interface ApiList<T> { items: T[]; total: number; page: number; limit: number }

// ── preview store ──────────────────────────────────────────────────────────
const now = new Date().toISOString()
const db: { customers: Customer[]; loyalty: LoyaltyConfig } = {
  customers: [
    {
      id: 'cust-demo-1', tenantId: 'tnt_demo', firstName: 'María', lastName: 'López',
      fullName: 'María López', source: 'ohsir_qr', notes: 'Identificado por QR — mesa Mesa 2',
      tags: [], lastVisitAt: now, totalOrdersCount: 2, totalSpentCents: '29800',
      loyaltyPoints: 100, loyaltyVisits: 2,
      phones: [{ id: 'p1', phone: '5512345678', label: 'mobile', isPrimary: true }],
      emails: [], addresses: [], fiscalEntities: [], createdAt: now, updatedAt: now,
    },
  ],
  loyalty: {
    tenantId: 'tnt_demo', enabled: true, pointsPerVisit: 10,
    tiers: [
      { id: 't1', name: 'Bronce', threshold: 0, color: '#cd7f32' },
      { id: 't2', name: 'Plata', threshold: 500, color: '#c0c0c0' },
      { id: 't3', name: 'Oro', threshold: 2000, color: '#d4af37' },
    ],
  },
}


/** Normaliza un contacto crudo del CRM a la forma del admin (campos opcionales). */
function toCustomer(raw: Partial<Customer> & { id: string; tenantId: string; firstName: string }): Customer {
  return {
    id: raw.id,
    tenantId: raw.tenantId,
    firstName: raw.firstName,
    lastName: raw.lastName ?? null,
    fullName: raw.fullName ?? [raw.firstName, raw.lastName].filter(Boolean).join(' '),
    source: raw.source ?? 'MANUAL',
    notes: raw.notes ?? null,
    tags: raw.tags ?? [],
    lastVisitAt: raw.lastVisitAt ?? null,
    totalOrdersCount: raw.totalOrdersCount ?? 0,
    totalSpentCents: raw.totalSpentCents ?? '0',
    loyaltyPoints: raw.loyaltyPoints ?? 0,
    loyaltyVisits: raw.loyaltyVisits ?? 0,
    phones: raw.phones ?? [],
    emails: raw.emails ?? [],
    addresses: raw.addresses ?? [],
    fiscalEntities: raw.fiscalEntities ?? [],
    createdAt: raw.createdAt ?? now,
    updatedAt: raw.updatedAt ?? now,
  }
}

export async function listCustomers(signal?: AbortSignal): Promise<Customer[]> {
  if (PREVIEW_STUB) return mock(db.customers)
  if (requireTenant() === null) return []
  // F2.5 / ADR-0009: lectura por el grafo (crm-contacts, tenant del JWT). Escrituras
  // (create/update) + config de lealtad siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminContactsQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.contacts.items ?? []).map((c) => toCustomer(c))
}

export async function getCustomer(id: string, signal?: AbortSignal): Promise<Customer> {
  if (PREVIEW_STUB) {
    const c = db.customers.find((x) => x.id === id)
    if (!c) throw new Error('Cliente no encontrado')
    return mock(c)
  }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminContactQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const c = res.data?.contact
  if (!c) throw new Error('Cliente no encontrado')
  return toCustomer(c)
}

// ── create / update ─────────────────────────────────────────────────────────
// El registro completo: personal + direcciones + entidades fiscales.

export interface CustomerInput {
  firstName: string
  lastName?: string
  notes?: string
  emails: Array<{ email: string; label?: string; isPrimary?: boolean }>
  phones: Array<{ phone: string; label?: string; isPrimary?: boolean }>
  addresses: Array<Omit<ContactAddress, 'id'>>
  fiscalEntities: Array<Omit<FiscalEntity, 'id'>>
}

export async function createCustomer(input: CustomerInput): Promise<Customer> {
  if (PREVIEW_STUB) {
    const c = toCustomer({
      id: `cust-${Date.now()}`, tenantId: authTenantId() || 'tnt_demo',
      firstName: input.firstName, lastName: input.lastName ?? null,
      source: 'MANUAL', notes: input.notes ?? null,
      phones: input.phones.map((p, i) => ({ id: `p${i}`, phone: p.phone, label: p.label ?? 'mobile', isPrimary: p.isPrimary ?? false })),
      emails: input.emails.map((e, i) => ({ id: `e${i}`, email: e.email, label: e.label ?? 'work', isPrimary: e.isPrimary ?? false })),
      addresses: input.addresses.map((a, i) => ({ ...a, id: `a${i}` })),
      fiscalEntities: input.fiscalEntities.map((f, i) => ({ ...f, id: `f${i}` })),
    })
    db.customers.push(c)
    return mock(c)
  }
  // F9.12 / ADR-0009: alta por el grafo (crm-contacts, tenant del JWT). Sin tenantId.
  const res = await gqlClient.mutation(AdminCreateContactMutation, {
    input: {
      firstName: input.firstName,
      ...(input.lastName ? { lastName: input.lastName } : {}),
      ...(input.notes ? { notes: input.notes } : {}),
      emails: input.emails,
      phones: input.phones,
      addresses: input.addresses,
      fiscalEntities: input.fiscalEntities,
    },
  }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.createContact
  if (!c) throw new Error('createCustomer: el grafo no devolvió el contacto')
  return toCustomer(c)
}

export async function updateCustomer(id: string, input: CustomerInput): Promise<Customer> {
  if (PREVIEW_STUB) {
    const idx = db.customers.findIndex((x) => x.id === id)
    if (idx < 0) throw new Error('Cliente no encontrado')
    const c = toCustomer({
      ...db.customers[idx],
      firstName: input.firstName, lastName: input.lastName ?? null, notes: input.notes ?? null,
      fullName: [input.firstName, input.lastName].filter(Boolean).join(' '),
      phones: input.phones.map((p, i) => ({ id: `p${i}`, phone: p.phone, label: p.label ?? 'mobile', isPrimary: p.isPrimary ?? false })),
      emails: input.emails.map((e, i) => ({ id: `e${i}`, email: e.email, label: e.label ?? 'work', isPrimary: e.isPrimary ?? false })),
      addresses: input.addresses.map((a, i) => ({ ...a, id: `a${i}` })),
      fiscalEntities: input.fiscalEntities.map((f, i) => ({ ...f, id: `f${i}` })),
    })
    db.customers[idx] = c
    return mock(c)
  }
  // F9.12 / ADR-0009: edición por el grafo (crm-contacts, tenant del JWT, guard IDOR).
  const res = await gqlClient.mutation(AdminUpdateContactMutation, {
    id,
    input: {
      firstName: input.firstName,
      lastName: input.lastName ?? '',
      notes: input.notes ?? '',
      emails: input.emails,
      phones: input.phones,
      addresses: input.addresses,
      fiscalEntities: input.fiscalEntities,
    },
  }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.updateContact
  if (!c) throw new Error('updateCustomer: el grafo no devolvió el contacto')
  return toCustomer(c)
}

// ── lealtad ──────────────────────────────────────────────────────────────────

export async function getLoyaltyConfig(signal?: AbortSignal): Promise<LoyaltyConfig> {
  if (PREVIEW_STUB) return mock(db.loyalty)
  // F6.2 R3b / ADR-0009: lectura por el grafo (crm-contacts, tenant del JWT). El
  // write saveLoyaltyConfig ya estaba en el grafo desde F9.12.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminLoyaltyConfigQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  const c = res.data?.loyaltyConfig
  if (!c) throw new Error('getLoyaltyConfig: el grafo no devolvió la config')
  return { tenantId: c.tenantId, enabled: c.enabled, pointsPerVisit: c.pointsPerVisit, tiers: c.tiers.map((t) => ({ ...t })) }
}

export async function saveLoyaltyConfig(next: {
  enabled: boolean
  pointsPerVisit: number
  tiers: Array<{ name: string; threshold: number; color: string | null }>
}): Promise<LoyaltyConfig> {
  if (PREVIEW_STUB) {
    db.loyalty = {
      ...db.loyalty,
      enabled: next.enabled,
      pointsPerVisit: next.pointsPerVisit,
      tiers: next.tiers.map((t, i) => ({ ...t, id: `t${i}` })),
    }
    return mock(db.loyalty)
  }
  // F9.12: config de lealtad por el grafo (tenant del JWT, NO arg).
  const res = await gqlClient.mutation(AdminSaveLoyaltyConfigMutation, {
    input: { enabled: next.enabled, pointsPerVisit: next.pointsPerVisit, tiers: next.tiers },
  }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.saveLoyaltyConfig
  if (!c) throw new Error('saveLoyaltyConfig: el grafo no devolvió la config')
  return { tenantId: c.tenantId, enabled: c.enabled, pointsPerVisit: c.pointsPerVisit, tiers: c.tiers }
}

/**
 * Deriva el nivel de un cliente: el tier de mayor umbral que sus puntos
 * alcanzan. null si la lealtad está apagada o no hay niveles configurados.
 */
export function deriveTier(points: number, config: LoyaltyConfig | null): LoyaltyTier | null {
  if (!config || !config.enabled || config.tiers.length === 0) return null
  const sorted = [...config.tiers].sort((a, b) => b.threshold - a.threshold)
  return sorted.find((t) => points >= t.threshold) ?? null
}

/** Etiqueta legible para el origen del contacto. */
export function sourceLabel(source: string): string {
  switch (source) {
    case 'ohsir_qr': return 'OhSir (QR)'
    case 'guest_checkin': return 'Check-in QR'
    case 'ERP_BRIDGE': return 'ERP'
    case 'MANUAL': return 'Manual'
    default: return source
  }
}
